
import React, { useState, useEffect } from 'react';
import { Teacher, Student, normalizeClassName } from '../types';
import { Layers, Plus, Trash2, GraduationCap, AlertTriangle, X, AlertCircle, Users, ArrowRightLeft, Search, Loader2, Pencil, StickyNote } from 'lucide-react';

interface Props {
  classes: string[];
  setClasses: (classes: string[]) => void;
  onAddClass?: (className: string) => void | Promise<void>;
  onDeleteClass: (clsName: string) => void | Promise<void>;
  onClearAllClasses?: () => void | Promise<void>;
  onRenameClass: (oldName: string, newName: string) => void | Promise<void>;
  teachers: Teacher[];
  students: Student[];
  onUpdateStudents: (students: Student[]) => void;
}

const ClassesManagementView: React.FC<Props> = ({ classes, setClasses, onAddClass, onDeleteClass, onClearAllClasses, onRenameClass, teachers, students, onUpdateStudents }) => {
  const [newClassName, setNewClassName] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [viewingClass, setViewingClass] = useState<string | null>(null);
  const [studentSearch, setStudentSearch] = useState('');
  
  // State untuk Edit Nama Kelas
  const [editingClass, setEditingClass] = useState<string | null>(null);
  const [editClassName, setEditClassName] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);

  // State untuk Konfirmasi Hapus
  const [confirmDelete, setConfirmDelete] = useState<{ isOpen: boolean; name: string; isBulk: boolean }>({
    isOpen: false,
    name: '',
    isBulk: false
  });

  const filteredStudents = students.filter(s => {
    if (s.class !== viewingClass) return false;
    const name = (s.name || '').toLowerCase();
    const nis = (s.nis || '').toLowerCase();
    const nisn = (s.nisn || '').toLowerCase();
    const term = (studentSearch || '').toLowerCase();
    return name.includes(term) || nis.includes(term) || nisn.includes(term);
  }).sort((a, b) => (a.name || '').localeCompare(b.name || '', 'id', { sensitivity: 'base' }));

  const handleMoveStudent = (student: Student, newClass: string) => {
    if (newClass === student.class) return;
    const updatedStudents = students.map(s => s.id === student.id ? { ...s, class: newClass } : s);
    onUpdateStudents(updatedStudents);
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (viewingClass && classes.length > 0 && !classes.includes(viewingClass)) {
      setViewingClass(null);
    }
  }, [classes, viewingClass]);

  const handleRenameSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingClass) return;
    const formattedNew = normalizeClassName(editClassName);
    if (!formattedNew) {
      alert('Nama kelas tidak boleh kosong!');
      return;
    }
    if (formattedNew === editingClass) {
      setEditingClass(null);
      return;
    }
    if (classes.includes(formattedNew)) {
      alert('Nama kelas sudah ada!');
      return;
    }
    setIsRenaming(true);
    try {
      await onRenameClass(editingClass, formattedNew);
      if (viewingClass === editingClass) {
        setViewingClass(formattedNew);
      }
      setEditingClass(null);
      setEditClassName('');
    } catch (err) {
      console.error('Failed to rename class:', err);
    } finally {
      setIsRenaming(false);
    }
  };

  const handleAddClass = async (e: React.FormEvent) => {
    e.preventDefault();
    const formattedName = normalizeClassName(newClassName);
    if (!formattedName) return;
    if (classes.includes(formattedName)) {
      alert('Nama kelas sudah ada!');
      return;
    }
    setIsSubmitting(true);
    try {
      if (onAddClass) {
        await onAddClass(formattedName);
      } else {
        const newClasses = [...classes, formattedName].sort();
        await setClasses(newClasses);
      }
      setNewClassName('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const triggerDelete = (clsName: string) => {
    setConfirmDelete({
      isOpen: true,
      name: clsName,
      isBulk: false
    });
  };

  const triggerClearAll = () => {
    if (classes.length === 0) return;
    setConfirmDelete({
      isOpen: true,
      name: `SEMUA (${classes.length}) ROMBEL`,
      isBulk: true
    });
  };

  const executeDelete = async () => {
    if (confirmDelete.isBulk) {
      if (onClearAllClasses) {
        await onClearAllClasses();
      } else {
        for (const cls of classes) {
          await onDeleteClass(cls);
        }
        await setClasses([]);
      }
    } else {
      await onDeleteClass(confirmDelete.name);
    }
    setConfirmDelete({ isOpen: false, name: '', isBulk: false });
  };

  return (
    <div className="space-y-8 animate-fade-in pb-10">
      <div className="bg-white dark:bg-slate-900 p-8 sm:p-10 rounded-[2.5rem] shadow-sm border border-slate-200 dark:border-slate-800">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8 mb-12">
          <div>
            <h2 className="text-3xl font-black text-slate-800 dark:text-white tracking-tight leading-none mb-3">Master Rombel</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">Manajemen daftar kelas SMPN 1 Kawali ({classes.length} Rombel).</p>
          </div>
          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
            <button
              onClick={() => setIsAddModalOpen(true)}
              className="flex items-center gap-2 px-6 py-3.5 bg-indigo-600 hover:bg-indigo-700 text-white font-black rounded-2xl shadow-xl shadow-indigo-500/25 active:scale-95 transition-all text-sm"
            >
              <Plus size={18} />
              <span>Tambah Kelas</span>
            </button>
            <button 
              onClick={triggerClearAll}
              className="px-6 py-3.5 bg-rose-50 dark:bg-rose-500/10 text-rose-600 font-black rounded-2xl hover:bg-rose-600 hover:text-white border border-rose-100 dark:border-rose-500/20 transition-all text-sm"
            >
              Hapus Semua
            </button>
          </div>
        </div>

        {classes.length === 0 ? (
          <div className="p-20 text-center bg-slate-50/50 dark:bg-slate-800/30 rounded-[3rem] border-2 border-dashed border-slate-200 dark:border-slate-800">
            <Layers className="mx-auto mb-4 text-slate-300" size={64} />
            <p className="text-slate-400 font-bold italic text-lg">Belum ada daftar rombel (kelas).</p>
            <p className="text-slate-400 text-sm mt-1">Masukkan nama kelas di atas untuk memulai.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {classes.map(cls => {
              const assignedTeachers = teachers.filter(t => t.assignedClasses.includes(cls));
              const classStudents = students.filter(s => s.class === cls);
              const isActive = assignedTeachers.length > 0 || classStudents.length > 0;
              
              return (
                <div key={cls} className="group relative bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800/80 rounded-[2.5pax] p-6 shadow-sm hover:shadow-xl hover:border-indigo-500/40 dark:hover:border-indigo-500/40 transition-all duration-300 flex flex-col justify-between overflow-hidden rounded-[2.5rem]">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/5 dark:bg-indigo-500/10 rounded-bl-full pointer-events-none transition-transform group-hover:scale-110"></div>
                  
                  <div>
                    <div className="flex justify-between items-start mb-6 relative z-10">
                      <div className="flex items-center gap-3.5">
                        <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-indigo-700 rounded-2xl flex items-center justify-center text-white font-black text-xl shadow-lg shadow-indigo-500/20 group-hover:scale-105 transition-transform">
                          {cls}
                        </div>
                        <div>
                          <h3 className="text-lg font-black text-slate-900 dark:text-white tracking-tight leading-tight">Kelas {cls}</h3>
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[9px] font-black tracking-wider uppercase mt-1 ${
                            isActive ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400 border border-emerald-200/50 dark:border-emerald-500/20' : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400'
                          }`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-emerald-500 animate-pulse' : 'bg-slate-400'}`}></span>
                            {isActive ? 'Aktif' : 'Kosong'}
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-1 bg-slate-50 dark:bg-slate-800/60 p-1 rounded-2xl border border-slate-200/60 dark:border-slate-700/60">
                        <button 
                          onClick={() => {
                            setEditingClass(cls);
                            setEditClassName(cls);
                          }}
                          className="p-2 text-slate-400 hover:text-amber-500 hover:bg-white dark:hover:bg-slate-700 rounded-xl transition-all"
                          title="Edit Nama Kelas"
                        >
                          <Pencil size={15} />
                        </button>
                        <button 
                          onClick={() => triggerDelete(cls)}
                          className="p-2 text-slate-400 hover:text-rose-500 hover:bg-white dark:hover:bg-slate-700 rounded-xl transition-all"
                          title="Hapus Kelas"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 mb-6 relative z-10">
                      <div className="p-3.5 bg-slate-50/80 dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-800">
                        <div className="flex items-center gap-2 mb-1">
                          <Users size={14} className="text-indigo-500" />
                          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Siswa</span>
                        </div>
                        <p className="text-lg font-black text-slate-800 dark:text-white">{classStudents.length}</p>
                      </div>

                      <div className="p-3.5 bg-slate-50/80 dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-800">
                        <div className="flex items-center gap-2 mb-1">
                          <GraduationCap size={14} className="text-indigo-500" />
                          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Guru</span>
                        </div>
                        <p className="text-lg font-black text-slate-800 dark:text-white">{assignedTeachers.length}</p>
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between relative z-10">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Data Rombel</span>
                    <button 
                      onClick={() => setViewingClass(cls)}
                      className="flex items-center gap-1.5 px-4 py-2 bg-indigo-50 dark:bg-indigo-500/10 hover:bg-indigo-600 hover:text-white text-indigo-600 dark:text-indigo-400 font-black rounded-xl text-xs transition-all shadow-sm active:scale-95"
                    >
                      <Users size={14} />
                      <span>Lihat Siswa</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* MODAL LIHAT SISWA */}
      {viewingClass && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-md animate-fade-in" onClick={() => setViewingClass(null)}></div>
          <div className="bg-white dark:bg-slate-900 w-full max-w-4xl max-h-[90vh] rounded-[3rem] shadow-2xl relative overflow-hidden animate-fade-in-up border border-slate-200 dark:border-slate-800 flex flex-col">
            <div className="p-8 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/50">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-indigo-600 rounded-2xl flex items-center justify-center text-white font-black text-lg shadow-lg shadow-indigo-500/20">
                  {viewingClass}
                </div>
                <div>
                  <h3 className="text-xl font-black text-slate-900 dark:text-white tracking-tight leading-none mb-1">Daftar Siswa Kelas {viewingClass}</h3>
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">{filteredStudents.length} Siswa Terdaftar</p>
                    <span className="text-slate-300 dark:text-slate-600">•</span>
                    <p className="text-xs font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                      <GraduationCap size={14} className="shrink-0" />
                      <span>Wali Kelas: {teachers.find(t => t.homeroomClass === viewingClass)?.name || 'Belum Ditentukan'}</span>
                    </p>
                  </div>
                </div>
              </div>
              <button 
                onClick={() => setViewingClass(null)}
                className="p-3 bg-white dark:bg-slate-800 text-slate-400 hover:text-slate-600 dark:hover:text-white rounded-2xl border border-slate-200 dark:border-slate-700 transition-all"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 border-b border-slate-100 dark:border-slate-800">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <input 
                  type="text" 
                  placeholder="Cari nama atau NIS siswa..."
                  value={studentSearch}
                  onChange={(e) => setStudentSearch(e.target.value)}
                  className="w-full pl-12 pr-6 py-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none focus:ring-4 focus:ring-indigo-500/10 dark:text-white font-bold text-sm transition-all"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {filteredStudents.length === 0 ? (
                <div className="py-20 text-center">
                  <Users className="mx-auto mb-4 text-slate-200" size={48} />
                  <p className="text-slate-400 font-bold italic">Tidak ada siswa ditemukan.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {filteredStudents.map(student => (
                    <div key={student.id} className="p-5 bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800 rounded-3xl flex items-center justify-between group hover:border-indigo-500/30 transition-all">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-black text-sm ${student.gender === 'Laki-laki' ? 'bg-blue-100 text-blue-600 dark:bg-blue-500/10' : 'bg-rose-100 text-rose-600 dark:bg-rose-500/10'}`}>
                          {student.name.charAt(0)}
                        </div>
                        <div>
                          <p className="text-sm font-black text-slate-800 dark:text-white leading-none mb-1">{student.name}</p>
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">NIS: {student.nis} {student.nisn ? `| NISN: ${student.nisn}` : ''}</p>
                          {(student.notes || student.specialNotes) && (
                            <p className="text-[11px] font-medium text-amber-700 dark:text-amber-300 mt-1 flex items-center gap-1">
                              <StickyNote size={11} className="text-amber-500 shrink-0" />
                              <span className="line-clamp-1">{student.notes || student.specialNotes}</span>
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <div className="relative group/select">
                          <select 
                            value={student.class}
                            onChange={(e) => handleMoveStudent(student, e.target.value)}
                            className="appearance-none pl-4 pr-10 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-[10px] font-black text-indigo-600 outline-none focus:ring-2 focus:ring-indigo-500/20 cursor-pointer transition-all"
                          >
                            {classes.map(c => (
                              <option key={c} value={c}>PINDAH KE {c}</option>
                            ))}
                          </select>
                          <ArrowRightLeft className="absolute right-3 top-1/2 -translate-y-1/2 text-indigo-400 pointer-events-none" size={12} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="p-8 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/50 flex justify-end">
              <button 
                onClick={() => setViewingClass(null)}
                className="px-8 py-3 bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-black rounded-2xl hover:scale-105 transition-all text-xs uppercase tracking-widest"
              >
                Selesai
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL EDIT NAMA KELAS */}
      {editingClass && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-md animate-fade-in" onClick={() => setEditingClass(null)}></div>
          <div className="bg-white dark:bg-slate-900 w-full max-w-sm rounded-[2.5rem] shadow-2xl relative overflow-hidden animate-fade-in-up border border-slate-200 dark:border-slate-800 p-8">
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center text-white font-black text-sm shadow-lg shadow-amber-500/20">
                  <Pencil size={20} />
                </div>
                <div>
                  <h3 className="text-lg font-black text-slate-900 dark:text-white leading-tight">Edit Nama Kelas</h3>
                  <p className="text-xs text-slate-400 font-medium">Ubah nama kelas {editingClass}</p>
                </div>
              </div>
              <button 
                onClick={() => setEditingClass(null)}
                className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-white rounded-xl transition-all"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleRenameSubmit} className="space-y-6">
              <div>
                <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">Nama Kelas Baru</label>
                <input 
                  type="text" 
                  value={editClassName}
                  onChange={(e) => setEditClassName(e.target.value)}
                  placeholder="Contoh: 7A, 8B..."
                  className="w-full px-5 py-3.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none focus:ring-4 focus:ring-amber-500/10 dark:text-white font-black text-base transition-all"
                  autoFocus
                />
              </div>

              <div className="flex gap-3">
                <button 
                  type="button" 
                  onClick={() => setEditingClass(null)}
                  className="flex-1 py-3.5 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-black rounded-2xl hover:bg-slate-200 dark:hover:bg-slate-700 transition-all text-xs uppercase tracking-widest"
                >
                  Batal
                </button>
                <button 
                  type="submit"
                  disabled={isRenaming || !editClassName.trim()}
                  className="flex-1 py-3.5 bg-amber-500 hover:bg-amber-600 disabled:bg-slate-300 text-white font-black rounded-2xl shadow-xl shadow-amber-500/20 active:scale-95 transition-all text-xs uppercase tracking-widest flex items-center justify-center gap-2"
                >
                  {isRenaming ? <Loader2 size={16} className="animate-spin" /> : null}
                  {isRenaming ? 'Menyimpan...' : 'Simpan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL TAMBAH KELAS */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-md animate-fade-in" onClick={() => setIsAddModalOpen(false)}></div>
          <div className="bg-white dark:bg-slate-900 w-full max-w-md rounded-[2.5rem] shadow-2xl relative overflow-hidden animate-fade-in-up border border-indigo-500/20">
            <div className="p-8">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 rounded-2xl flex items-center justify-center font-black">
                    <Plus size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-black text-slate-900 dark:text-white tracking-tight">Tambah Rombel / Kelas</h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">Masukkan nama kelas baru (contoh: 7E, 8D, 9A).</p>
                  </div>
                </div>
                <button 
                  onClick={() => setIsAddModalOpen(false)}
                  className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-xl"
                >
                  <X size={20} />
                </button>
              </div>

              <form onSubmit={async (e) => {
                e.preventDefault();
                const formattedName = newClassName.trim().toUpperCase();
                if (!formattedName) return;
                if (classes.includes(formattedName)) {
                  alert('Nama kelas sudah ada!');
                  return;
                }
                setIsSubmitting(true);
                try {
                  if (onAddClass) {
                    await onAddClass(formattedName);
                  } else {
                    const newClasses = [...classes, formattedName].sort();
                    await setClasses(newClasses);
                  }
                  setNewClassName('');
                  setIsAddModalOpen(false);
                } finally {
                  setIsSubmitting(false);
                }
              }} className="space-y-6">
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Nama Kelas</label>
                  <input 
                    type="text" 
                    placeholder="Contoh: 9A, 7E, VIII-B"
                    value={newClassName}
                    onChange={(e) => setNewClassName(e.target.value)}
                    className="w-full px-5 py-3.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none focus:ring-4 focus:ring-indigo-500/10 dark:text-white font-black text-base transition-all"
                    autoFocus
                  />
                </div>

                <div className="flex gap-3">
                  <button 
                    type="button" 
                    onClick={() => setIsAddModalOpen(false)}
                    className="flex-1 py-3.5 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-black rounded-2xl hover:bg-slate-200 dark:hover:bg-slate-700 transition-all text-xs uppercase tracking-widest"
                  >
                    Batal
                  </button>
                  <button 
                    type="submit"
                    disabled={isSubmitting || !newClassName.trim()}
                    className="flex-1 py-3.5 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 text-white font-black rounded-2xl shadow-xl shadow-indigo-500/20 active:scale-95 transition-all text-xs uppercase tracking-widest flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? <Loader2 size={16} className="animate-spin" /> : null}
                    {isSubmitting ? 'Menyimpan...' : 'Tambah Kelas'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* CUSTOM MODAL KONFIRMASI HAPUS KELAS */}
      {confirmDelete.isOpen && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-md animate-fade-in" onClick={() => setConfirmDelete({...confirmDelete, isOpen: false})}></div>
          <div className="bg-white dark:bg-slate-900 w-full max-w-sm rounded-[2.5rem] shadow-2xl relative overflow-hidden animate-fade-in-up border border-rose-500/20 text-center">
            <div className="p-10">
              <div className="w-20 h-20 bg-rose-50 dark:bg-rose-500/10 text-rose-500 rounded-3xl flex items-center justify-center mx-auto mb-6">
                <AlertTriangle size={40} />
              </div>
              <h3 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight mb-2">Hapus Rombel?</h3>
              <p className="text-slate-500 dark:text-slate-400 text-sm font-medium mb-8 leading-relaxed">
                Anda akan menghapus <span className="text-rose-500 font-black">{confirmDelete.name}</span>. 
                {confirmDelete.isBulk ? (
                  " Ini akan menghapus SELURUH daftar kelas dan plotting guru yang ada."
                ) : (
                  " Data guru dan siswa yang terhubung dengan kelas ini akan terdampak."
                )}
              </p>
              <div className="flex flex-col gap-3">
                <button 
                  type="button" 
                  onClick={executeDelete} 
                  className="w-full py-4 bg-rose-500 text-white font-black rounded-2xl hover:bg-rose-600 active:scale-95 transition-all shadow-xl shadow-rose-500/20 uppercase tracking-widest text-xs"
                >
                  Ya, Hapus Sekarang
                </button>
                <button 
                  type="button" 
                  onClick={() => setConfirmDelete({...confirmDelete, isOpen: false})} 
                  className="w-full py-4 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-black rounded-2xl hover:bg-slate-200 dark:hover:bg-slate-700 transition-all uppercase tracking-widest text-xs"
                >
                  Batalkan
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassesManagementView;
